import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n";

const AC_ISSUES = [
  "Kall luft",
  "Varm luft",
  "Ljud",
  "Lukter",
  "Annat",
] as const;

interface ACSectionProps {
  ac: { problem?: string; issue?: string; other?: string } | null;
  onACChange: (ac: {
    problem?: string;
    issue?: string;
    other?: string;
  }) => void;
  onNext: () => void;
}

export const ACSection = ({ ac, onACChange, onNext }: ACSectionProps) => {
  const [otherText, setOtherText] = useState(ac?.other || "");
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const form = ct.form ?? {};
  const v = ct.pdf?.values ?? ({} as Record<string, string>);
  const opt =
    (v as { optionValues?: Record<string, string> }).optionValues ?? {};

  const handleResultChange = (problem: string) => {
    if (problem === "no" || problem === "not_tested") {
      onACChange({ problem });
    } else {
      onACChange({ problem, issue: undefined, other: undefined });
    }
  };

  const handleIssueChange = (issue: string) => {
    if (issue === "Annat") {
      onACChange({ ...ac, issue, other: otherText });
    } else {
      onACChange({ ...ac, issue, other: undefined });
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    if (ac?.issue === "Annat") {
      onACChange({ ...ac, other: text });
    }
  };

  const showNextButton =
    ac?.problem === "yes" && ac?.issue === "Annat" && otherText.trim();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium text-sm mb-3">{form.questionAC}</h4>
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          <Button
            onClick={() => handleResultChange("yes")}
            variant="formOption"
            className={`${
              ac?.problem === "yes"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.yes}
          </Button>
          <Button
            onClick={() => handleResultChange("no")}
            variant="formOption"
            className={`${
              ac?.problem === "no"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.no}
          </Button>
          <Button
            onClick={() => handleResultChange("not_tested")}
            variant="formOption"
            className={`${
              ac?.problem === "not_tested"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.notTested}
          </Button>
        </div>

        {ac?.problem === "yes" && (
          <div className="space-y-3">
            <h5 className="font-medium text-sm">{form.questionWhichProblem}</h5>
            <div className="grid grid-cols-2 gap-1.5">
              {AC_ISSUES.map((issue) => (
                <Button
                  key={issue}
                  onClick={() => handleIssueChange(issue)}
                  variant="formOption"
                  className={`${
                    ac?.issue === issue
                      ? "border-accent border-2"
                      : "border-gray-400"
                  }`}
                >
                  {opt[issue] ?? issue}
                </Button>
              ))}
            </div>

            {ac?.issue === "Annat" && (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={form.placeholderDescribeProblem}
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        {showNextButton && (
          <Button className="w-full mt-4" onClick={onNext}>
            {form.next}
          </Button>
        )}
      </div>
    </div>
  );
};
